import React, { FC } from "react";
import styled from "styled-components";
import { P2 } from "components/atoms/Paragraph";
import { Accordion, AccordionItem } from "components/molecules/Accordion";
import { Modal } from "components/organisms/Modal";
import { CookieTable, advertisingData, analyticsData, necessaryData } from "./tables";
import { color } from "@styles";
import { CustomAccordionTitle as AccordionTitle } from "./CustomAccordionTitle";
import { mobile } from "components/templates/Breakpoints";
import { CookiePreferences } from "../types";
import { convertNewlinesToBreaks } from "utils/string";
import { useLocaleData } from "hooks/useLocaleData";
import data from "../translations";

interface Props {
  showModal: boolean;
  onClose: () => void;
  preferences: Map<keyof CookiePreferences, boolean>;
  onPreferencesUpdate: (preference: string, isEnabled: boolean) => void;
  onAcceptAll: () => void;
  onRejectAll: () => void;
  onSaveSettings: () => void;
}

export const ManagePreferences: FC<Props> = ({
  showModal,
  onClose,
  preferences,
  onPreferencesUpdate,
  onAcceptAll,
  onRejectAll,
  onSaveSettings,
}) => {
  const { cookiesModal, cookiesTable } = useLocaleData(data);
  return (
    <Modal
      header={cookiesModal.heading}
      closeOnClickOutside={false}
      closeModal={() => {
        onClose();
        onAcceptAll();
      }}
      showModal={showModal}
      primaryButton={cookiesModal.acceptAll}
      secondaryButton={cookiesModal.saveSettings}
      tertiaryButtonLeft={cookiesModal.reject}
      onPrimaryButtonClick={onAcceptAll}
      onSecondaryButtonClick={onSaveSettings}
      onTertiaryButtonLeftClick={onRejectAll}
      maxWidth={"51rem"}
    >
      <AccordionContainer>
        <Accordion>
          <AccordionItem id="necessary-cookies" title={cookiesModal.necessaryHeader}>
            <P2 pt="0" pb="1rem">
              {convertNewlinesToBreaks(cookiesModal.necessaryDesc)}
            </P2>
            <CookieTable
              data={necessaryData}
              cookieHeader={cookiesTable.cookie}
              providerHeader={cookiesTable.provider}
            />
          </AccordionItem>
          <AccordionItem
            id="analytics-cookies"
            title={
              <AccordionTitle
                id="analytics-cookies"
                title={cookiesModal.analyticsHeader}
                checked={preferences.get("analytics")}
                onCheck={(checked) => onPreferencesUpdate("analytics", checked)}
              />
            }
          >
            <P2 pt="0" pb="1rem">
              {convertNewlinesToBreaks(cookiesModal.analyticsDesc)}
            </P2>
            <CookieTable
              data={analyticsData}
              cookieHeader={cookiesTable.cookie}
              providerHeader={cookiesTable.provider}
            />
          </AccordionItem>
          <AccordionItem
            id="advertising-cookies"
            title={
              <AccordionTitle
                id="advertising-cookies"
                title={cookiesModal.advertisingHeader}
                checked={preferences.get("advertising")}
                onCheck={(checked) => {
                  onPreferencesUpdate("advertising", checked);
                }}
              />
            }
          >
            <P2 pt="0" pb="1rem">
              {convertNewlinesToBreaks(cookiesModal.advertisingDesc)}
            </P2>
            <CookieTable
              data={advertisingData}
              cookieHeader={cookiesTable.cookie}
              providerHeader={cookiesTable.provider}
            />
          </AccordionItem>
          <AccordionItem
            id="personalisation-cookies"
            title={
              <AccordionTitle
                id="personalisation-cookies"
                title={cookiesModal.personalisationHeader}
                checked={preferences.get("personalisation")}
                onCheck={(checked) => {
                  onPreferencesUpdate("personalisation", checked);
                }}
              />
            }
          >
            <P2 pt="0" pb="1rem">
              {convertNewlinesToBreaks(cookiesModal.personalisationDesc)}
            </P2>
            <StyledItalic pt="0">
              {convertNewlinesToBreaks(cookiesModal.personalisationMessage)}
            </StyledItalic>
          </AccordionItem>
          <AccordionItem
            id="security-cookies"
            title={
              <AccordionTitle
                id="security-cookies"
                title={cookiesModal.securityHeader}
                checked={preferences.get("security")}
                onCheck={(checked) => {
                  onPreferencesUpdate("security", checked);
                }}
              />
            }
          >
            <P2 pt="0" pb="1rem">
              {convertNewlinesToBreaks(cookiesModal.securityDesc)}
            </P2>
            <StyledItalic pt="0">
              {convertNewlinesToBreaks(cookiesModal.securityMessage)}
            </StyledItalic>
          </AccordionItem>
        </Accordion>
      </AccordionContainer>
    </Modal>
  );
};

const AccordionContainer = styled.div`
  border-top: 1px solid ${color.B230};
  border-bottom: 1px solid ${color.B230};
  padding: 2rem;
  ${mobile} {
    max-height: 100vh;
  }
  @media (max-width: 300px) {
    padding: 0.25rem;
  }
`;

const StyledItalic = styled(P2)`
  font-style: italic;
`;
